import { useState } from "react";
import axios from "axios";
import { useLang } from "@/contexts/LangContext";
import SectionReveal from "@/components/SectionReveal";
import { Loader2, Send, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = () => {
  const { t } = useLang();
  const tt = t.contact;

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    travel_dates: "",
    travelers: 2,
    purpose: "",
    budget: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      // Compose enriched message that captures all extra fields (backend accepts name/email/subject/message only)
      const enriched = [
        form.message,
        "",
        "— Trip details —",
        `Country: ${form.country || "—"}`,
        `WhatsApp: ${form.phone || "—"}`,
        `Travel dates: ${form.travel_dates || "—"}`,
        `Travelers: ${form.travelers || "—"}`,
        `Purpose: ${form.purpose || "—"}`,
        `Budget: ${form.budget || "—"}`,
      ].join("\n");
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: form.subject || form.purpose || "New inquiry",
        message: enriched,
      });
      setStatus("success");
      setForm({
        name: "",
        email: "",
        phone: "",
        country: "",
        travel_dates: "",
        travelers: 2,
        purpose: "",
        budget: "",
        subject: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-[#F7F5F0] py-24 md:py-32"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <SectionReveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.3em] text-[#C62828] font-semibold mb-4">
            ✦ {tt.eyebrow}
          </p>
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#121212] leading-[1.05]">
            {tt.title}
          </h2>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed">{tt.subtitle}</p>
        </SectionReveal>

        <SectionReveal delay={0.15} className="mt-14">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="space-y-6 bg-white border border-black/5 p-8 md:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <Field label={tt.name}>
                <input
                  data-testid="contact-name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="ip"
                />
              </Field>
              <Field label={tt.email}>
                <input
                  data-testid="contact-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="ip"
                />
              </Field>
              <Field label="Country">
                <input
                  data-testid="contact-country"
                  type="text"
                  placeholder="e.g. United Arab Emirates"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  className="ip"
                />
              </Field>
              <Field label="WhatsApp number">
                <input
                  data-testid="contact-phone"
                  type="tel"
                  placeholder="+971 ..."
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="ip"
                />
              </Field>
              <Field label="Travel dates">
                <input
                  data-testid="contact-travel-dates"
                  type="text"
                  placeholder="e.g. 12 Apr – 22 Apr 2026"
                  value={form.travel_dates}
                  onChange={(e) => setForm({ ...form, travel_dates: e.target.value })}
                  className="ip"
                />
              </Field>
              <Field label="Number of travelers">
                <input
                  data-testid="contact-travelers"
                  type="number"
                  min={1}
                  max={50}
                  value={form.travelers}
                  onChange={(e) => setForm({ ...form, travelers: e.target.value })}
                  className="ip"
                />
              </Field>
              <Field label="Purpose of visit">
                <select
                  data-testid="contact-purpose"
                  value={form.purpose}
                  onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                  className="ip"
                >
                  <option value="">Select...</option>
                  <option>Leisure / Tourism</option>
                  <option>Luxury Travel</option>
                  <option>Business / Canton Fair</option>
                  <option>Medical / Wellness</option>
                  <option>Mandarin Learning Program</option>
                  <option>Educational / School Trip</option>
                  <option>Family Trip</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Budget range (per traveler)">
                <select
                  data-testid="contact-budget"
                  value={form.budget}
                  onChange={(e) => setForm({ ...form, budget: e.target.value })}
                  className="ip"
                >
                  <option value="">Select...</option>
                  <option>Under $1,500</option>
                  <option>$1,500 – $3,000</option>
                  <option>$3,000 – $6,000</option>
                  <option>$6,000 – $12,000</option>
                  <option>$12,000+</option>
                </select>
              </Field>
            </div>
            <Field label={tt.subject}>
              <input
                data-testid="contact-subject"
                type="text"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="ip"
              />
            </Field>
            <Field label={tt.message}>
              <textarea
                data-testid="contact-message"
                required
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="ip resize-none"
              />
            </Field>

            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={status === "loading"}
              className="group bg-[#C62828] hover:bg-[#B71C1C] disabled:opacity-60 text-white px-8 py-4 uppercase tracking-wider text-sm font-semibold inline-flex items-center gap-3 transition-all duration-300"
            >
              {status === "loading" ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> ...
                </>
              ) : (
                <>
                  <Send size={16} /> {tt.submit}
                </>
              )}
            </button>

            {status === "success" && (
              <p
                data-testid="contact-success"
                className="flex items-center gap-2 text-[#2E5D4B] text-sm"
              >
                <CheckCircle2 size={16} /> {tt.success}
              </p>
            )}
            {status === "error" && (
              <p data-testid="contact-error" className="text-[#C62828] text-sm">
                {tt.error}
              </p>
            )}
          </form>
        </SectionReveal>
      </div>

      <style>{`
        .ip {
          width: 100%;
          background: #F7F5F0;
          border: 1px solid rgba(0,0,0,0.12);
          padding: 0.875rem 1rem;
          color: #121212;
          font-size: 0.95rem;
          outline: none;
          transition: border-color 200ms;
        }
        .ip:focus { border-color: #C62828; }
      `}</style>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs uppercase tracking-[0.2em] font-semibold text-[#4A4A4A] mb-2">
      {label}
    </span>
    {children}
  </label>
);

export default ContactForm;
